import { useEffect, useState } from 'react';
import {
  exportSaleReceiptCsv,
  exportSaleReceiptExcel,
  exportSaleReceiptPdf,
  printSaleReceipt,
} from '../exportReceipt';
import { formatCurrency } from '../receptionistData';
import { withReceiptPrintSerial } from '../../../services/receptionist/receiptSerial';
import { InvoiceTermsNotes } from '../InvoiceTermsNotes';

function ReceiptRow({ label, value, highlight = false }) {
  return (
    <div
      className={`rec-payment-receipt-row${highlight ? ' rec-payment-receipt-row-highlight' : ''}`}
    >
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function SaleReceiptSummary({ receipt }) {
  const complimentary = Boolean(receipt.complimentary);
  const remainingInvoice = Boolean(receipt.isRemainingClear);
  return (
    <div className="rec-payment-receipt-preview">
      <ReceiptRow label="CR. No." value={receipt.invoiceId || '—'} />
      {!complimentary && (
        <ReceiptRow label="Payment ID" value={receipt.paymentId} />
      )}
      <ReceiptRow label="Sale Type" value={receipt.saleLabel || receipt.saleTitle} />
      <ReceiptRow label="Customer" value={receipt.customer} />
      <ReceiptRow
        label="Mobile"
        value={receipt.phone || receipt.mobile || receipt.customerPhone || '—'}
      />
      <ReceiptRow label="Member Serial No" value={receipt.gymId || '—'} />
      <ReceiptRow
        label={receipt.itemFieldLabel || 'Package'}
        value={receipt.item || receipt.package || '—'}
      />
      <ReceiptRow label="Duration" value={receipt.durationLabel || '—'} />
      <ReceiptRow label="Start Date" value={receipt.startDate} />
      <ReceiptRow label="End Date" value={receipt.endDate} />
      {remainingInvoice && (
        <ReceiptRow label="Sale / Selected Date" value={receipt.saleDate || receipt.startDate || '—'} />
      )}
      <ReceiptRow
        label={remainingInvoice ? 'Payment Date' : 'Transaction Date'}
        value={receipt.date}
      />
      <ReceiptRow
        label={remainingInvoice ? 'Package / Sale Price' : 'Total Amount'}
        value={complimentary ? 'Complimentary' : receipt.totalAmountDisplay}
      />
      {remainingInvoice && Number(receipt.packagePrice) > 0 && (
        <ReceiptRow label="Package Price" value={receipt.packagePriceDisplay} />
      )}
      {remainingInvoice && Number(receipt.registrationFee) > 0 && (
        <ReceiptRow label="Registration Fee" value={receipt.registrationFeeDisplay} />
      )}

      {complimentary ? (
        <ReceiptRow label="Payment Type" value="Complimentary" />
      ) : receipt.splitLines?.length ? (
        receipt.splitLines.map((line) => (
          <ReceiptRow key={line.label} label={line.label} value={line.value} />
        ))
      ) : receipt.paymentType ? (
        <ReceiptRow label="Payment Type" value={receipt.paymentType} />
      ) : null}

      {!complimentary && remainingInvoice && receipt.balanceBeforeDisplay && (
        <ReceiptRow label="Remaining Before" value={receipt.balanceBeforeDisplay} />
      )}

      {!complimentary && receipt.cumulativePaidDisplay && receipt.installmentDisplay && (
        <ReceiptRow label="Total Paid (to date)" value={receipt.cumulativePaidDisplay} />
      )}

      {!complimentary && (
        <ReceiptRow
          label={remainingInvoice ? 'Remaining After' : 'Pending Amount'}
          value={receipt.totalLeftDisplay || formatCurrency(receipt.totalLeft ?? receipt.pendingAmount ?? 0)}
        />
      )}

      <ReceiptRow
        label={remainingInvoice ? 'Remaining Paid' : 'Amount Paid'}
        value={
          complimentary
            ? 'Complimentary'
            : (receipt.installmentDisplay || receipt.paymentAmountDisplay)
        }
        highlight
      />

      {receipt.paymentNote && (
        <ReceiptRow label="Note" value={receipt.paymentNote} />
      )}
      <InvoiceTermsNotes />
    </div>
  );
}

export default function SaleReceiptModal({ receipt, onClose }) {
  const [liveReceipt, setLiveReceipt] = useState(() => (
    receipt ? withReceiptPrintSerial(receipt) : null
  ));

  useEffect(() => {
    if (!receipt) {
      setLiveReceipt(null);
      return;
    }
    setLiveReceipt(withReceiptPrintSerial(receipt));
  }, [receipt]);

  if (!liveReceipt) return null;

  const runPrint = () => {
    const prepared = printSaleReceipt(liveReceipt);
    if (prepared) setLiveReceipt(prepared);
  };

  const runExport = (fn) => {
    const prepared = fn(liveReceipt);
    if (prepared) setLiveReceipt(prepared);
  };

  return (
    <div className="subscription-modal-overlay" onClick={onClose}>
      <div
        className="subscription-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="subscription-modal-header">
          <h2 className="subscription-modal-title">
            {liveReceipt.isRemainingClear ? 'Remaining Payment Invoice' : 'Sale Receipt'}
          </h2>
          <button
            type="button"
            className="subscription-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <SaleReceiptSummary receipt={liveReceipt} />
        <div className="subscription-form-actions">
          <button type="button" className="owner-btn owner-btn-primary" onClick={runPrint}>
            Print Thermal Receipt
          </button>
          <button
            type="button"
            className="owner-btn owner-btn-secondary owner-btn-sm"
            onClick={() => runExport(exportSaleReceiptPdf)}
          >
            Export PDF
          </button>
          <button
            type="button"
            className="owner-btn owner-btn-secondary owner-btn-sm"
            onClick={() => runExport(exportSaleReceiptCsv)}
          >
            Export CSV
          </button>
          <button
            type="button"
            className="owner-btn owner-btn-secondary owner-btn-sm"
            onClick={() => runExport(exportSaleReceiptExcel)}
          >
            Export Excel
          </button>
          <button type="button" className="owner-btn owner-btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
